import type { GrowthStage, PlantType, Season } from "../types";

/**
 * 植物の成長計算。
 *
 * ゲーム本体（simulateDay）とショップ・種ドロワーの「約N日」表示は、必ずここを通す。
 * 以前は本体が baseGrowthRate だけで進み、表示は stageDurations の単純合計だったため、
 * 両者が 4〜7 倍ずれていた。計算をこの 1 か所に集約して、その乖離が再発しないようにする。
 */

/** 1 ゲーム日ぶんのゲーム内分数。stageDurations と同じ単位。 */
export const MINUTES_PER_GAME_DAY = 1440;

/** 種から収穫可能（fruiting）になるまでの並び。 */
export const GROWTH_ORDER = ["seed", "sprout", "growing", "budding", "flowering", "fruiting"] as const;

/** 適期のほうが進みやすい後半の段階。 */
export const LATE_STAGES: readonly GrowthStage[] = ["flowering", "fruiting"];

const SEASON_ORDER: readonly Season[] = ["spring", "summer", "autumn", "winter"];

/**
 * 植え付け直後の成長段階。
 * 苗・苗木はショップで種より高く売られているのに、これまで種と同じ「seed」から始まっており、
 * 価格差に見合う違いが無かった。育った状態から始めることで差を実挙動に反映する。
 */
export function initialStage(plant: PlantType): GrowthStage {
  return plant.plantingForm === "sapling" ? "growing" : plant.plantingForm === "seedling" ? "sprout" : "seed";
}

export interface GrowthConditions {
  /** 適期（preferredSeasons）に入っているか。 */
  preferred: boolean;
  /** 生育可能な季節（seasonsAvailable）に入っているか。 */
  available: boolean;
  /** 土壌水分が適正で健康なら true。 */
  wellTended: boolean;
}

export interface GrowthState {
  stage: GrowthStage;
  stageProgress: number;
}

export interface GrowthResult extends GrowthState {
  /** その日に進んだ段階の数。GP 付与やクエスト記録の回数に使う。 */
  stagesAdvanced: number;
}

function seasonFactor(conditions: GrowthConditions): number {
  return conditions.preferred ? 1 : conditions.available ? 0.55 : 0.15;
}

/**
 * 1 ゲーム日ぶん成長させる。
 *
 * その日に使える生育時間を、段階ごとの所要時間へ順番に充てていく。段階によって
 * 所要時間が違うため、余りは正規化した進捗ではなく「分」で持ち越す必要がある。
 */
export function advanceGrowthDay(plant: PlantType, state: GrowthState, conditions: GrowthConditions): GrowthResult {
  let { stage, stageProgress } = state;
  let stagesAdvanced = 0;
  let remaining = MINUTES_PER_GAME_DAY;
  const care = conditions.wellTended ? 1 : 0.4;
  const season = seasonFactor(conditions);

  for (let guard = 0; guard < GROWTH_ORDER.length && remaining > 0; guard += 1) {
    const index = (GROWTH_ORDER as readonly string[]).indexOf(stage);
    const nextStage = index >= 0 ? GROWTH_ORDER[index + 1] : undefined;
    const duration = plant.stageDurations[stage] ?? 0;
    if (!nextStage || duration <= 0) break;

    const late = LATE_STAGES.includes(nextStage) && !conditions.preferred ? 0.35 : 1;
    const rate = plant.baseGrowthRate * season * care * late;
    if (rate <= 0) break;

    const needed = ((1 - stageProgress) * duration) / rate;
    if (remaining < needed) {
      stageProgress += (remaining * rate) / duration;
      break;
    }
    remaining -= needed;
    stageProgress = 0;
    stage = nextStage;
    stagesAdvanced += 1;
  }
  return { stage, stageProgress, stagesAdvanced };
}

/** 多年草・樹木は冬に休眠し、その間は成長しない。 */
function dormantInWinter(plant: PlantType, season: Season): boolean {
  return season === "winter" && (plant.lifeCycle === "perennial" || plant.lifeCycle === "tree");
}

/** 指定した季節に植えた場合、収穫できるようになるまでのゲーム内日数。 */
export function daysToHarvestFrom(plant: PlantType, daysPerSeason: number, startSeason: Season): number {
  let seasonIndex = Math.max(0, SEASON_ORDER.indexOf(startSeason));
  let dayInSeason = 1;
  let state: GrowthState = { stage: initialStage(plant), stageProgress: 0 };

  for (let day = 1; day <= 400; day += 1) {
    const season = SEASON_ORDER[seasonIndex] ?? "spring";
    if (!dormantInWinter(plant, season)) {
      state = advanceGrowthDay(plant, state, {
        preferred: plant.preferredSeasons.includes(season),
        available: plant.seasonsAvailable.includes(season),
        wellTended: true,
      });
      if (state.stage === "fruiting") return day;
    }
    dayInSeason += 1;
    if (dayInSeason > daysPerSeason) {
      dayInSeason = 1;
      seasonIndex = (seasonIndex + 1) % SEASON_ORDER.length;
    }
  }
  return 400;
}

/** 植え付けに最も適した季節（収穫までが最短になる季節）。 */
export function bestPlantingSeason(plant: PlantType, daysPerSeason: number): Season {
  const candidates = plant.seasonsAvailable.length > 0 ? plant.seasonsAvailable : SEASON_ORDER;
  let best = candidates[0] ?? "spring";
  let bestDays = Number.POSITIVE_INFINITY;
  for (const season of candidates) {
    const days = daysToHarvestFrom(plant, daysPerSeason, season);
    if (days < bestDays) {
      bestDays = days;
      best = season;
    }
  }
  return best;
}

/**
 * 最適な季節に植えて完璧に世話をした場合、収穫できるようになるまでのゲーム内日数。
 * 季節の移り変わりと冬の休眠も織り込むので、1 季節で育ちきらない品種は実際に待つ日数が返る。
 */
export function estimateDaysToHarvest(plant: PlantType, daysPerSeason: number): number {
  return daysToHarvestFrom(plant, daysPerSeason, bestPlantingSeason(plant, daysPerSeason));
}
